import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MdOutlineDashboard } from "react-icons/md";
import { IoPersonOutline, IoReceiptOutline } from "react-icons/io5";
import { SlCalender } from "react-icons/sl";
import { TbCalendarTime, TbClockPlus, TbReportAnalytics } from "react-icons/tb";
import { HiOutlineClipboardList, HiOutlineDotsHorizontal } from "react-icons/hi";
import { AnimatePresence, motion } from "framer-motion";
import { useUser } from "../../context/UserContext";
import { LuCalendarMinus, LuClipboardPlus } from "react-icons/lu";
import { usePendingRequests } from "../../context/PendingRequestContext";
import { usePendingCompOffRequests } from "../../context/PendingCompOffRequestContext";
import axios from "axios";
import { HiOutlineBars3 } from "react-icons/hi2";
import api from "../../utils/api";
import { usePendingOnDutyRequests } from "../../context/PendingOnDutyRequestContext";
import { LucideClipboardPlus, Megaphone, TentTree } from "lucide-react";
import { GrUserWorker } from "react-icons/gr";

function E_BottomNavBar() {
  const { currentUser, loading } = useUser();
  const userRole = currentUser?.role;
  const [showMore, setShowMore] = useState(false);
  const dropdownRef = useRef();
  const navigate = useNavigate();
  const { pendingCount, updatePendingCount } = usePendingRequests();
  const { pendingCompOffCount, updatePendingCompOffCount } = usePendingCompOffRequests();
  const { pendingOndutyCount, updatePendingOndutyCount } = usePendingOnDutyRequests();
  const [requests, setRequests] = useState(0);
  const baseUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchPendingCount = async () => {
      if (userRole === "MANAGER" && currentUser?.id) {
        try {
          const leaveResponse = await api.get(
            `/api/leaves/pending-count?managerId=${currentUser?.id}`
          );
          const compOffResponse = await api.get(
            `/api/compoffs/pending-count?managerId=${currentUser?.id}`
          );
          const onDutyResponse = await api.get(
            `/api/onduty/pending-count?managerId=${currentUser?.id}`
          );
          updatePendingOndutyCount(onDutyResponse.data);
          updatePendingCount(leaveResponse.data);
          updatePendingCompOffCount(compOffResponse.data);
        } catch (error) {
          console.error("Error fetching pending count:", error);
        }
      }
    };

    if (!loading && currentUser) {
      fetchPendingCount();
      const interval = setInterval(fetchPendingCount, 300000);
      return () => clearInterval(interval);
    }
  }, [userRole, currentUser, baseUrl, updatePendingCount, updatePendingCompOffCount, loading]);

  useEffect(() => {
    setRequests(pendingCount + pendingCompOffCount + pendingOndutyCount);
  }, [pendingCount, pendingCompOffCount, pendingOndutyCount]);

  const navLinks = [
    {
      name: "Dashboard",
      link: "/employee/dashboard",
      icon: <MdOutlineDashboard className="text-2xl" />,
    },
    {
      name: "Pay Slips",
      link: "/employee/payslips",
      icon: <IoReceiptOutline className="text-2xl" />,
    },
    {
      name: "Attendance",
      link: "/employee/attendance",
      icon: <SlCalender className="text-2xl" />,
    },
    {
      name: "Announcements",
      link: "/employee/events/annoucements",
      icon: <Megaphone className="text-2xl" />,
    }
  ];

  const extraLinks = [
    {
      name: "Holidays",
      link: "/employee/events/holidays",
      icon: <TentTree className="text-2xl" />,
    },
    {
      name: "Leaves",
      link: "/employee/leaves",
      icon: <LuCalendarMinus className="text-2xl" />,
    },
    {
      name: "Comp Off",
      link: "/employee/comp-off",
      icon: <TbClockPlus className="text-2xl" />,
    },
    {
      name: "Timesheet",
      link: "/employee/simple-timesheet",
      icon: <TbCalendarTime className="text-2xl" />,
    },
    {
      name: "On duty",
      link: "/employee/on-duty",
      icon: <GrUserWorker className="text-2xl" />,
    }
  ];

  if (currentUser?.role === "MANAGER" && currentUser?.office === "SPC") {
    extraLinks.push({
      name: "Comp Requests",
      link: "/employee/comp-off-requests",
      icon: <LuClipboardPlus className="text-2xl" />,
      badge: pendingCompOffCount > 0 ? pendingCompOffCount : null
    });

    extraLinks.push({
      name: "Leave Requests",
      link: "/employee/leave-requests",
      icon: <HiOutlineClipboardList className="text-2xl" />,
      badge: pendingCount > 0 ? pendingCount : null
    });

    extraLinks.push({
      name: "On duty Requests",
      link: "/employee/on-duty-requests",
      icon: <TbReportAnalytics className="text-2xl" />,
      badge: pendingOndutyCount > 0 ? pendingOndutyCount : null
    });
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMore(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMore]);

  return (
    <div className="relative w-full h-full flex justify-center items-center p-2">
      {/* Enhanced Glassmorphic Nav Bar with Slate Theme */}
      <div className="relative flex justify-around items-center px-3 py-3 w-full max-w-md bg-gradient-to-r from-slate-900/95 via-slate-800/95 to-slate-900/95 backdrop-blur-2xl border border-slate-600/50 shadow-[0_8px_32px_0_rgba(15,23,42,0.6)] rounded-[28px]">
        
        {/* Subtle slate-toned glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-700/10 via-slate-600/10 to-slate-700/10 rounded-[28px] blur-xl" />
        <div className="absolute -inset-[1px] bg-slate-600/5 rounded-[28px] blur-2xl" />
        
        <div className="relative flex justify-around items-center w-full gap-3">
          {navLinks.map((item, index) => (
            <NavLink
              key={index}
              to={item.link}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center w-16 h-16 rounded-[20px] transition-all duration-300 group
                ${isActive 
                  ? "text-white" 
                  : "text-slate-400 hover:text-slate-200"}`
              }
            >
              {({ isActive }) => (
                <>
                  {/* Active indicator with smooth movement */}
                  {isActive && (
                    <motion.div
                      layoutId="navPill"
                      className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-[20px] shadow-xl shadow-orange-500/40"
                      transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                    />
                  )}
                  
                  {/* Hover effect */}
                  {!isActive && (
                    <motion.div 
                      className="absolute inset-0 bg-slate-700/0 group-hover:bg-slate-700/40 rounded-[20px] transition-all duration-300 backdrop-blur-sm"
                      whileHover={{ scale: 1.05 }}
                    />
                  )}
                  
                  {/* Icon */}
                  <motion.span 
                    className="relative z-10 drop-shadow-lg"
                    whileHover={{ scale: 1.15 }} 
                    whileTap={{ scale: 0.9 }}
                  >
                    {item.icon}
                  </motion.span>
                  
                  {/* Active indicator dot */}
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg shadow-white/50 z-10"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {/* More Button */}
          <div className="relative flex flex-col items-center" ref={dropdownRef}>
            <motion.button
              onClick={() => setShowMore(!showMore)}
              className={`relative flex flex-col items-center justify-center w-16 h-16 rounded-[20px] transition-all duration-300 group overflow-hidden
                ${showMore 
                  ? "text-white" 
                  : "text-slate-400 hover:text-slate-200"}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Active/hover background */}
              {showMore && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-[20px] shadow-xl shadow-orange-500/40"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                />
              )}
              
              {!showMore && (
                <motion.div 
                  className="absolute inset-0 bg-slate-700/0 group-hover:bg-slate-700/40 rounded-[20px] transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                />
              )}
              
              <motion.span
                className="relative z-10 drop-shadow-lg"
                animate={{ rotate: showMore ? 90 : 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <HiOutlineBars3 className="text-2xl" />
              </motion.span>
              
              {/* Badge */}
              <AnimatePresence>
                {requests > 0 && (
                  <motion.span
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute -top-1 -right-1 flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[10px] font-bold text-white bg-gradient-to-br from-red-500 via-red-600 to-pink-600 rounded-full shadow-lg shadow-red-500/50 border-2 border-slate-900 z-20"
                  >
                    {requests > 99 ? "99+" : requests}
                  </motion.span>
                )}
              </AnimatePresence>
              
              {/* Active indicator dot */}
              {showMore && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg shadow-white/50 z-10"
                />
              )}
            </motion.button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showMore && (
                <>
                  {/* Backdrop blur overlay */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/20 rounded-3xl backdrop-blur-[2px] z-40"
                    onClick={() => setShowMore(false)}
                  />
                  
                  <motion.div
                    initial={{ opacity: 0, scale: 0.85, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.85, y: 20 }}
                    transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
                    className="absolute bottom-20 right-0 w-72 bg-gradient-to-br from-slate-900/98 via-slate-800/98 to-slate-900/98 rounded-3xl backdrop-blur-2xl border border-slate-600/50 shadow-[0_20px_60px_0_rgba(15,23,42,0.8)] rounded-[24px] overflow-hidden z-50"
                  >
                    {/* Decorative slate gradient line at top */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-slate-500 to-transparent" />
                    
                    {/* Header */}
                    <div className="relative px-5 py-4 bg-gradient-to-r from-slate-700/20 via-slate-700/10 to-transparent border-b border-slate-600/50">
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-700/10 to-transparent" />
                      <h3 className="relative text-sm font-semibold text-white tracking-wide">Quick Actions</h3>
                      <p className="relative text-xs text-slate-400 mt-1">Manage your requests & activities</p>
                    </div>
                    
                    {/* Menu items - Removed all item animations */}
                    <div className="p-2.5 max-h-96 overflow-y-auto custom-scrollbar">
                      {extraLinks.map((item, index) => (
                        <button
                          key={index}
                          className="flex items-center gap-4 px-4 py-3.5 rounded-[16px] hover:bg-slate-700/50 active:bg-slate-700/70 transition-all duration-200 w-full group relative overflow-hidden"
                          onClick={() => {
                            setShowMore(false);
                            navigate(item.link);
                          }}
                        >
                          {/* Icon container */}
                          <span className="relative z-10 flex items-center justify-center w-10 h-10 rounded-xl bg-slate-800/50 group-hover:bg-slate-700/70 text-slate-400 group-hover:text-slate-200 transition-all duration-300">
                            {item.icon}
                          </span>
                          
                          <span className="flex-1 text-left text-sm font-medium text-slate-300 group-hover:text-white transition-colors relative z-10">
                            {item.name}
                          </span>
                          
                          {/* Badge */}
                          {item.badge && (
                            <span className="relative z-10 min-w-[28px] h-7 px-2.5 flex items-center justify-center bg-gradient-to-br from-red-500 via-red-600 to-pink-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-red-500/30 border border-red-400/20">
                              {item.badge > 99 ? "99+" : item.badge}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.3);
          border-radius: 10px;
          margin: 8px 0;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, rgba(251, 146, 60, 0.4), rgba(251, 146, 60, 0.2));
          border-radius: 10px;
          transition: background 0.2s;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, rgba(251, 146, 60, 0.6), rgba(251, 146, 60, 0.4));
        }
      `}</style>
    </div>
  );
}

export default E_BottomNavBar;
